import React, { useEffect } from "react";
import { useGetEmployeeLeaveDetailsQuery } from "../../slices/employeesApiSlice";
import LeaveSummary from "./LeaveSummary";
import { useDispatch, useSelector } from "react-redux";
import { addRelatedNos } from "./../../slices/leaveClaimRecordsSlice";
import { recordsType } from "../../constants";
import LeaveRecordDetails from "./LeaveRecordDetails";
import RequestFailed from "../../components/alerts/RequestFailed";
import Loader from "../../components/ui/Loader";
import NoRecords from "../../components/alerts/NoRecords";
import { logout } from "./../../slices/authSlice";
import PropTypes from "prop-types";

function LeaveRecord({
    leaveNo,
    organizationId,
    organizationAccess,
    employeeId,
    print,
    fetchingLeave, // Callback prop from parent
    user,
    leaveDetailsData,
    skipQuery = false,
}) {
    const dispatch = useDispatch();
    const isODSEnabled = useSelector((state) => state.app.flags.isODSEnabled);
    const cachedLeaveDetails = useSelector(
        (state) =>
            state.employeeClaimLeave.odsLeaveDetailsData?.[String(leaveNo)]
    );
    const shouldUseCachedLeave = isODSEnabled && !!cachedLeaveDetails;

    const {
        data: apiEmployeeLeaveDetails = null,
        isError,
        isLoading,
        isFetching,
        error,
    } = useGetEmployeeLeaveDetailsQuery(
        isODSEnabled
            ? { leaveNo, organizationId }
            : { leaveNo, organizationId, employeeId },
        {
            skip: skipQuery || shouldUseCachedLeave,
        }
    );

    const employeeLeaveDetails =
        leaveDetailsData ??
        (shouldUseCachedLeave ? cachedLeaveDetails : apiEmployeeLeaveDetails);

    useEffect(() => {
        if (error && error.status === 401) {
            dispatch(logout());
        }
    }, [error, dispatch]);

    useEffect(() => {
        if (employeeLeaveDetails === null) {
            return;
        }

        const leaveSummary =
            employeeLeaveDetails.LeaveSummary.length === 1
                ? employeeLeaveDetails.LeaveSummary[0]
                : null;

        if (leaveSummary && leaveSummary.RelatedSTDClaims) {
            const relatedSTDClaims = leaveSummary.RelatedSTDClaims.split(",");

            dispatch(
                addRelatedNos({
                    nos: relatedSTDClaims,
                    type: recordsType.CLAIM,
                })
            );
        }
    }, [employeeLeaveDetails, dispatch]);

    useEffect(() => {
        if (!print) fetchingLeave(isFetching); // Call the callback function

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetching]);

    return (
        <div id={"leave-container-" + leaveNo}>
            {isFetching && <Loader loading={isLoading} />}
            {isError && <RequestFailed label="leave error" />}

            {employeeLeaveDetails !== null &&
                !isFetching &&
                !isError &&
                employeeLeaveDetails.Header.length === 0 && (
                    <NoRecords
                        attribute1="claim leave"
                        attribute3="claimleave_error"
                        message="No Records Found"
                        label="leave record"
                    />
                )}

            {employeeLeaveDetails !== null &&
                !isFetching &&
                !isError &&
                employeeLeaveDetails.Header.length > 0 && (
                    <>
                        <LeaveSummary
                            leaveDetailsEmp={employeeLeaveDetails}
                            organizationAccess={organizationAccess}
                            user={user}
                        />

                        <LeaveRecordDetails
                            leaveDetailsEmp={employeeLeaveDetails}
                            print={print}
                            leaveNo={leaveNo}
                        />
                    </>
                )}
        </div>
    );
}

LeaveRecord.propTypes = {
    leaveNo: PropTypes.string,
    organizationId: PropTypes.number,
    organizationAccess: PropTypes.object,
    employeeId: PropTypes.string.isRequired,
    print: PropTypes.bool,
    fetchingLeave: PropTypes.func,
    user: PropTypes.object,
    leaveDetailsData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    skipQuery: PropTypes.bool,
};

export default React.memo(LeaveRecord);
